import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FiSearch, FiRefreshCw, FiAlertCircle, FiX,
  FiPackage, FiFileText, FiAlertTriangle, FiCheckCircle,
  FiClock, FiCalendar, FiFilter,
} from 'react-icons/fi';
import { getVimDashboard, getPoJourney, getInvoiceJourney } from '../../utils/ocrApi';

const STATUS_CONFIG = {
  PO_CREATED:     { label: 'PO Created',     bg: '#f5f7fa', color: '#4a5568', border: '#e2e8f0' },
  GR_PARTIAL:     { label: 'GR Partial',     bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
  GR_COMPLETED:   { label: 'GR Completed',   bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  INVOICE_POSTED: { label: 'Invoice Posted', bg: '#f3e8ff', color: '#6b21a8', border: '#c4b5fd' },
  BLOCKED:        { label: 'Blocked',        bg: '#ffebee', color: '#b71c1c', border: '#ef9a9a' },
  APPROVED:       { label: 'Approved',       bg: '#e8f5e9', color: '#1b5e20', border: '#a5d6a7' },
  PAID:           { label: 'Paid',           bg: '#dcfce7', color: '#14532d', border: '#6ee7b7' },
  CANCELLED:      { label: 'Cancelled',      bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status || '—', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

function fmtDate(str) {
  if (!str) return '—';
  try { return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return str; }
}

function fmtCurrency(val, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
}

// ── Timeline ──────────────────────────────────────────────────────────────────
const Timeline = ({ timeline }) => {
  if (!timeline?.length) return <p style={{ color: 'var(--muted)', fontSize: '0.82rem', padding: '8px 0' }}>No timeline data.</p>;

  return (
    <div className="inv-timeline">
      {timeline.map((step, i) => {
        const isDone    = step.completed && !step.active;
        const isCurrent = step.completed && step.active;
        return (
          <React.Fragment key={i}>
            <div className={`inv-tl-step${isDone ? ' done' : isCurrent ? ' current' : ''}`}>
              <div className="inv-tl-circle">{isDone ? '✓' : isCurrent ? '●' : '○'}</div>
              <div className="inv-tl-label">{step.label}</div>
              {step.timestamp && <div className="inv-tl-date">{fmtDate(step.timestamp)}</div>}
            </div>
            {i < timeline.length - 1 && (
              <div className={`inv-tl-connector${step.completed ? ' done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── GR Table ──────────────────────────────────────────────────────────────────
const GRTable = ({ rows }) => (
  <div className="inv-modal-table-wrap">
    <table className="inv-modal-table">
      <thead><tr><th>GR Doc</th><th>GR Date</th><th>Material</th><th>Plant</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
      <tbody>
        {rows.map((gr, i) => (
          <tr key={i}>
            <td>{gr.material_doc}</td><td>{fmtDate(gr.gr_date)}</td>
            <td>{gr.material}</td><td>{gr.plant}</td>
            <td style={{ textAlign: 'right' }}>{gr.quantity}</td>
            <td style={{ textAlign: 'right' }}>{fmtCurrency(gr.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── PO Journey Modal ──────────────────────────────────────────────────────────
const POModal = ({ vendorCode, poNumber, onClose, onOpenInvoice }) => {
  const [loading, setLoading] = useState(true);
  const [data,    setData]    = useState(null);
  const [error,   setError]   = useState('');

  useEffect(() => {
    getPoJourney(poNumber, vendorCode)
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [poNumber, vendorCode]);

  return ReactDOM.createPortal(
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal" style={{ maxWidth: 920 }} onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-header">
          <h3>PO Journey — {poNumber}</h3>
          <button className="inv-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="inv-modal-body">
          {loading && <div className="inv-loading"><div className="inv-spinner" /> Loading journey…</div>}
          {error   && <div className="inv-error-banner"><FiAlertCircle size={14} />{error}</div>}
          {data && !loading && (
            <>
              <div className="inv-info-strip">
                <div className="inv-info-row"><span className="inv-info-label">Status</span><span className="inv-info-value"><StatusBadge status={data.vim_status} /></span></div>
                <div className="inv-info-row"><span className="inv-info-label">Total Amount</span><span className="inv-info-value">{fmtCurrency(data.total_amount, data.currency)}</span></div>
                <div className="inv-info-row"><span className="inv-info-label">Company</span><span className="inv-info-value">{data.company_code || '—'}</span></div>
                <div className="inv-info-row"><span className="inv-info-label">PO Date</span><span className="inv-info-value">{fmtDate(data.po_date)}</span></div>
                {data.block_reason && <div className="inv-info-row"><span className="inv-info-label">Block Reason</span><span className="inv-info-value" style={{ color: 'var(--red)' }}>{data.block_reason}</span></div>}
              </div>
              {data.next_action && <div className="inv-next-action">{data.next_action}</div>}
              <div className="inv-section-heading">Process Timeline</div>
              <Timeline timeline={data.timeline} />
              {data.line_items?.length > 0 && (
                <>
                  <div className="inv-section-heading">Line Items</div>
                  <div className="inv-modal-table-wrap">
                    <table className="inv-modal-table">
                      <thead><tr><th>Item</th><th>Material</th><th>Description</th><th style={{ textAlign: 'right' }}>Qty</th><th>Unit</th><th style={{ textAlign: 'right' }}>Amount</th><th>Delivery</th></tr></thead>
                      <tbody>
                        {data.line_items.map((item, i) => (
                          <tr key={i}>
                            <td>{item.item_number}</td><td>{item.material}</td>
                            <td>{item.material_description || '—'}</td>
                            <td style={{ textAlign: 'right' }}>{item.order_quantity}</td>
                            <td>{item.unit}</td>
                            <td style={{ textAlign: 'right' }}>{fmtCurrency(item.net_amount, item.currency)}</td>
                            <td>{fmtDate(item.delivery_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {data.gr_history?.length > 0 && (
                <><div className="inv-section-heading">Goods Receipt History</div><GRTable rows={data.gr_history} /></>
              )}
              {data.linked_invoices?.length > 0 && (
                <>
                  <div className="inv-section-heading">Linked Invoices — click to view</div>
                  <div className="inv-modal-table-wrap">
                    <table className="inv-modal-table">
                      <thead><tr><th>Invoice Doc</th><th>Invoice Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th>Aging</th></tr></thead>
                      <tbody>
                        {data.linked_invoices.map((inv, i) => (
                          <tr key={i} style={{ cursor: 'pointer' }} onClick={() => { onClose(); onOpenInvoice(inv); }}>
                            <td style={{ color: 'var(--blue-1)', fontWeight: 700 }}>{inv.invoice_doc}</td>
                            <td>{fmtDate(inv.invoice_date)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtCurrency(inv.gross_amount, inv.currency)}</td>
                            <td><StatusBadge status={inv.vim_status} /></td>
                            <td>{inv.aging_days != null ? `${inv.aging_days}d` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="inv-modal-footer">PO {poNumber} · {data?.company_code || 'N/A'}</div>
      </div>
    </div>,
    document.body
  );
};

// ── Invoice Journey Modal ─────────────────────────────────────────────────────
const InvoiceModal = ({ vendorCode, invoiceDoc, fiscalYear, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data,    setData]    = useState(null);
  const [error,   setError]   = useState('');

  useEffect(() => {
    getInvoiceJourney(invoiceDoc, fiscalYear, vendorCode)
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [invoiceDoc, fiscalYear, vendorCode]);

  return ReactDOM.createPortal(
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal" style={{ maxWidth: 920 }} onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-header">
          <h3>Invoice Journey — {invoiceDoc}</h3>
          <button className="inv-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="inv-modal-body">
          {loading && <div className="inv-loading"><div className="inv-spinner" /> Loading journey…</div>}
          {error   && <div className="inv-error-banner"><FiAlertCircle size={14} />{error}</div>}
          {data && !loading && (
            <>
              <div className="inv-info-strip">
                <div className="inv-info-row"><span className="inv-info-label">Status</span><span className="inv-info-value"><StatusBadge status={data.vim_status} /></span></div>
                <div className="inv-info-row"><span className="inv-info-label">Gross Amount</span><span className="inv-info-value">{fmtCurrency(data.gross_amount, data.currency)}</span></div>
                {data.tax_amount != null && <div className="inv-info-row"><span className="inv-info-label">Tax</span><span className="inv-info-value">{fmtCurrency(data.tax_amount, data.currency)}</span></div>}
                <div className="inv-info-row"><span className="inv-info-label">Payment Terms</span><span className="inv-info-value">{data.payment_terms || '—'}</span></div>
                <div className="inv-info-row"><span className="inv-info-label">Invoice Date</span><span className="inv-info-value">{fmtDate(data.invoice_date)}</span></div>
                {data.external_invoice_ref && <div className="inv-info-row"><span className="inv-info-label">Ext. Ref</span><span className="inv-info-value">{data.external_invoice_ref}</span></div>}
                {data.block_reason && <div className="inv-info-row"><span className="inv-info-label">Block Reason</span><span className="inv-info-value" style={{ color: 'var(--red)' }}>{data.block_reason}</span></div>}
              </div>
              {data.next_action && <div className="inv-next-action">{data.next_action}</div>}
              {data.payment_info?.available && (
                <div className="inv-payment-banner">
                  <div className="inv-payment-banner-title">Payment Received</div>
                  <div className="inv-payment-details">
                    <span><strong>Doc:</strong> {data.payment_info.payment_doc}</span>
                    <span><strong>Date:</strong> {fmtDate(data.payment_info.payment_date)}</span>
                    <span><strong>Amount:</strong> {fmtCurrency(data.payment_info.payment_amount, data.payment_info.currency)}</span>
                  </div>
                </div>
              )}
              <div className="inv-section-heading">Process Timeline</div>
              <Timeline timeline={data.timeline} />
              {data.line_items?.length > 0 && (
                <>
                  <div className="inv-section-heading">Line Items</div>
                  <div className="inv-modal-table-wrap">
                    <table className="inv-modal-table">
                      <thead><tr><th>Item</th><th>PO Number</th><th>Material</th><th style={{ textAlign: 'right' }}>Qty</th><th>Unit</th><th style={{ textAlign: 'right' }}>Amount</th><th>Tax Code</th></tr></thead>
                      <tbody>
                        {data.line_items.map((item, i) => (
                          <tr key={i}>
                            <td>{item.item_number}</td><td>{item.po_number}</td>
                            <td>{item.material}</td>
                            <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                            <td>{item.unit}</td>
                            <td style={{ textAlign: 'right' }}>{fmtCurrency(item.net_amount, item.currency)}</td>
                            <td>{item.tax_code || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {data.gr_history?.length > 0 && (
                <><div className="inv-section-heading">Goods Receipt History</div><GRTable rows={data.gr_history} /></>
              )}
            </>
          )}
        </div>
        <div className="inv-modal-footer">Invoice {invoiceDoc} · FY {fiscalYear}</div>
      </div>
    </div>,
    document.body
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const OCRVendorMonitor = () => {
  const [vendorCode, setVendorCode] = useState(localStorage.getItem('vendorCode') || '');
  const [inputCode,  setInputCode]  = useState(localStorage.getItem('vendorCode') || '');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [dashboard,  setDashboard]  = useState(null);
  const [activeTab,  setActiveTab]  = useState('invoices');

  // Filter state
  const [invFilter,    setInvFilter]    = useState('');
  const [poFilter,     setPoFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [poModal,  setPoModal]  = useState(null);
  const [invModal, setInvModal] = useState(null);

  const fetchDashboard = useCallback(async (force = false) => {
    const code = vendorCode.trim();
    if (!code) { setError('Please enter a vendor code.'); return; }
    setLoading(true); setError('');
    try {
      const data = await getVimDashboard(code, force ? { force_refresh: true } : {});
      if (!data.success) throw new Error(data.error || 'API returned failure');
      setDashboard(data);
      setStatusFilter('');
      localStorage.setItem('vendorCode', code);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [vendorCode]);

  useEffect(() => { if (vendorCode) fetchDashboard(); }, [vendorCode]); // eslint-disable-line

  const handleSearch = () => setVendorCode(inputCode.trim());

  const summary = dashboard?.summary;

  const KPI_ITEMS = summary ? [
    { label: 'Total POs',        value: summary.total_pos,        icon: <FiPackage size={18} />,       kpiColor: '#1565c0', kpiBg: '#e3f2fd',  status: '' },
    { label: 'Total Invoices',   value: summary.total_invoices,   icon: <FiFileText size={18} />,      kpiColor: '#6b21a8', kpiBg: '#f3e8ff',  status: '' },
    { label: 'Blocked',          value: summary.blocked_count,    icon: <FiAlertTriangle size={18} />, kpiColor: '#b71c1c', kpiBg: '#ffebee',  status: 'BLOCKED'  },
    { label: 'Paid',             value: summary.paid_count,       icon: <FiCheckCircle size={18} />,   kpiColor: '#14532d', kpiBg: '#dcfce7',  status: 'PAID'     },
    { label: 'Pending',          value: summary.pending_count,    icon: <FiClock size={18} />,         kpiColor: '#92400e', kpiBg: '#fef3c7',  status: ''         },
    { label: 'Overdue',          value: summary.overdue_count,    icon: <FiCalendar size={18} />,      kpiColor: '#c2410c', kpiBg: '#ffedd5',  status: ''         },
  ] : [];

  // Filtered data
  const invoices = (dashboard?.invoices || []).filter((inv) => {
    const q = invFilter.toLowerCase();
    const matchesText = !q || (inv.invoice_doc?.toLowerCase().includes(q) || inv.po_number?.toLowerCase().includes(q) || inv.external_invoice_ref?.toLowerCase().includes(q));
    const matchesStatus = !statusFilter || inv.vim_status === statusFilter;
    return matchesText && matchesStatus;
  });

  const purchaseOrders = (dashboard?.purchase_orders || []).filter((po) => {
    const q = poFilter.toLowerCase();
    return !q || (po.po_number?.toLowerCase().includes(q));
  });

  return (
    <div>
      {/* Search bar */}
      <div className="inv-vendor-bar">
        <label>Vendor Code</label>
        <input
          value={inputCode}
          onChange={(e) => setInputCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="e.g. 600000"
        />
        <button className="inv-btn primary" onClick={handleSearch} disabled={loading}>
          {loading ? <><div className="inv-spinner" />Loading…</> : <><FiSearch size={14} />Search</>}
        </button>
        <button className="inv-btn ghost" onClick={() => fetchDashboard(true)} disabled={loading || !vendorCode}>
          <FiRefreshCw size={14} /> Refresh
        </button>
        {dashboard?.generated_at && (
          <span style={{ fontSize: '0.74rem', color: 'var(--muted)', marginLeft: 'auto' }}>
            Updated {new Date(dashboard.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {error && <div className="inv-error-banner"><FiAlertCircle size={14} />{error}</div>}

      {/* KPI Cards */}
      {summary && (
        <div className="inv-kpi-grid">
          {KPI_ITEMS.map((k, i) => (
            <div
              key={k.label}
              className={`inv-kpi-card${statusFilter === k.status && k.status ? ' kpi-active' : ''}`}
              style={{ '--card-delay': `${i * 55}ms`, '--kpi-color': k.kpiColor, '--kpi-bg': k.kpiBg }}
              onClick={() => {
                if (!k.status) return;
                setStatusFilter((prev) => prev === k.status ? '' : k.status);
                setActiveTab('invoices');
              }}
              title={k.status ? `Filter by ${k.label}` : undefined}
            >
              <div className="inv-kpi-icon">{k.icon}</div>
              <div className="inv-kpi-body">
                <div className="inv-kpi-label">{k.label}</div>
                <div className="inv-kpi-value">{k.value ?? '—'}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active status filter pill */}
      {statusFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <FiFilter size={13} style={{ color: 'var(--sub)' }} />
          <span style={{ fontSize: '0.8rem', color: 'var(--sub)', fontWeight: 600 }}>Filtered by:</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, background: '#ffebee', border: '1px solid #ef9a9a', color: '#b71c1c', fontSize: '0.78rem', fontWeight: 700 }}>
            {statusFilter}
            <button onClick={() => setStatusFilter('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b71c1c', padding: 0, display: 'flex' }}>
              <FiX size={12} />
            </button>
          </span>
        </div>
      )}

      {/* Sub-tabs */}
      {dashboard && (
        <>
          <div className="inv-sub-tabs">
            <button className={`inv-sub-tab${activeTab === 'invoices' ? ' active' : ''}`} onClick={() => setActiveTab('invoices')}>
              <FiFileText size={13} /> Invoices ({invoices.length})
            </button>
            <button className={`inv-sub-tab${activeTab === 'pos' ? ' active' : ''}`} onClick={() => setActiveTab('pos')}>
              <FiPackage size={13} /> Purchase Orders ({purchaseOrders.length})
            </button>
          </div>

          {/* Table filter bar */}
          <div className="inv-filter-bar">
            <div className="inv-filter-input">
              <FiSearch size={14} />
              <input
                placeholder={activeTab === 'invoices' ? 'Search invoices…' : 'Search POs…'}
                value={activeTab === 'invoices' ? invFilter : poFilter}
                onChange={(e) => activeTab === 'invoices' ? setInvFilter(e.target.value) : setPoFilter(e.target.value)}
              />
            </div>
            <span className="inv-filter-count">
              {activeTab === 'invoices' ? `${invoices.length} of ${dashboard.invoices?.length || 0}` : `${purchaseOrders.length} of ${dashboard.purchase_orders?.length || 0}`}
            </span>
          </div>

          {/* Invoices table */}
          {activeTab === 'invoices' && (
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Invoice Doc</th><th>PO Number</th><th>Invoice Date</th>
                    <th>Amount</th><th>Status</th><th>Aging</th><th>Next Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length ? invoices.map((inv, i) => (
                    <tr key={i} style={{ cursor: 'pointer', '--row-delay': `${i * 25}ms` }}
                      onClick={() => setInvModal({ invoiceDoc: inv.invoice_doc, fiscalYear: inv.fiscal_year })}>
                      <td>
                        <span style={{ color: 'var(--blue-1)', fontWeight: 700 }}>{inv.invoice_doc}</span>
                        {inv.external_invoice_ref && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{inv.external_invoice_ref}</div>}
                      </td>
                      <td style={{ fontWeight: 500 }}>{inv.po_number}</td>
                      <td>{fmtDate(inv.invoice_date)}</td>
                      <td style={{ fontWeight: 600 }}>{fmtCurrency(inv.gross_amount, inv.currency)}</td>
                      <td><StatusBadge status={inv.vim_status} /></td>
                      <td style={{ fontWeight: 600, color: (inv.aging_days || 0) > 30 ? '#b71c1c' : 'var(--sub)' }}>
                        {inv.aging_days != null ? `${inv.aging_days}d` : '—'}
                      </td>
                      <td style={{ fontSize: 11, maxWidth: 200, whiteSpace: 'normal', color: 'var(--sub)' }}>{inv.next_action}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7}>
                      <div className="inv-empty" style={{ padding: 32 }}>
                        <div className="inv-empty-icon"><FiFileText size={22} /></div>
                        <p>{invFilter || statusFilter ? 'No invoices match the current filter.' : 'No invoices found for this vendor.'}</p>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* POs table */}
          {activeTab === 'pos' && (
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr><th>PO Number</th><th>PO Date</th><th>Items</th><th>Total Amount</th><th>Status</th><th>Next Action</th></tr>
                </thead>
                <tbody>
                  {purchaseOrders.length ? purchaseOrders.map((po, i) => (
                    <tr key={i} style={{ cursor: 'pointer', '--row-delay': `${i * 25}ms` }}
                      onClick={() => setPoModal({ poNumber: po.po_number })}>
                      <td style={{ color: 'var(--blue-1)', fontWeight: 700 }}>{po.po_number}</td>
                      <td>{fmtDate(po.po_date)}</td>
                      <td style={{ fontWeight: 500 }}>{po.item_count}</td>
                      <td style={{ fontWeight: 600 }}>{fmtCurrency(po.total_amount, po.currency)}</td>
                      <td><StatusBadge status={po.vim_status} /></td>
                      <td style={{ fontSize: 11, maxWidth: 200, whiteSpace: 'normal', color: 'var(--sub)' }}>{po.next_action}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6}>
                      <div className="inv-empty" style={{ padding: 32 }}>
                        <div className="inv-empty-icon"><FiPackage size={22} /></div>
                        <p>{poFilter ? 'No POs match the current filter.' : 'No purchase orders found for this vendor.'}</p>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!dashboard && !loading && !error && (
        <div className="inv-card">
          <div className="inv-empty" style={{ padding: 60 }}>
            <div className="inv-empty-icon"><FiSearch size={26} /></div>
            <strong>Enter a Vendor Code</strong>
            <p>Search by vendor code to view the full invoice lifecycle — POs, GRs, invoices, and payments.</p>
          </div>
        </div>
      )}

      {/* Modals */}
      {poModal && (
        <POModal
          vendorCode={vendorCode} poNumber={poModal.poNumber}
          onClose={() => setPoModal(null)}
          onOpenInvoice={(inv) => setInvModal({ invoiceDoc: inv.invoice_doc, fiscalYear: inv.fiscal_year })}
        />
      )}
      {invModal && (
        <InvoiceModal
          vendorCode={vendorCode} invoiceDoc={invModal.invoiceDoc} fiscalYear={invModal.fiscalYear}
          onClose={() => setInvModal(null)}
        />
      )}
    </div>
  );
};

export default OCRVendorMonitor;
 